import { Box, Button, CircularProgress, Fade, Modal, TextField, Typography } from "@mui/material";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import React, { useEffect } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 4,
};

const PURPLE_HOVER = "#7A2074";

const TONE_STYLES = {
  danger: { icon: WarningAmberRounded, color: purple, tint: purpleLight },
  success: { icon: CheckCircleRounded, color: "#1E6446", tint: "#E7F5EC" },
};

export default function BlockAppUser({
  open,
  onDialogClose,
  updateUserAccess,
  title,
  submitBtnLabel,
  tone = "danger",
  submitting = false,
}: {
  open: boolean;
  onDialogClose: () => void;
  updateUserAccess: (remarks: string) => void;
  title: string;
  submitBtnLabel: string;
  tone?: "danger" | "success";
  submitting?: boolean;
}) {
  const [comments, setComments] = React.useState("");
  const [error, setError] = React.useState("");
  const { icon: ToneIcon, color, tint } = TONE_STYLES[tone];

  function submit() {
    setError("");
    if (comments.length === 0) {
      setError("Enter comments");
    } else {
      updateUserAccess(comments);
    }
  }

  useEffect(() => {
    if (open) {
      setComments("");
      setError("");
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={onDialogClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box sx={style}>
          <Box display="flex" flexDirection="column" alignItems="center" pb={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 52,
                height: 52,
                borderRadius: "50%",
                bgcolor: tint,
                mb: 1.5,
              }}
            >
              <ToneIcon sx={{ color, fontSize: 28 }} />
            </Box>
            <Typography
              id="transition-modal-title"
              align="center"
              variant="body1"
              fontSize={17}
              fontWeight={700}
              color={black}
            >
              {title}
            </Typography>
          </Box>
          <TextField
            multiline
            minRows={4}
            fullWidth
            label="Add a comment"
            placeholder="Reason for this action"
            value={comments}
            onChange={({ target: { value } }) => {
              setComments(value);
            }}
            error={Boolean(error)}
            helperText={error}
            sx={{
              mt: 1.5,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
          <Box mt={3} display="flex" justifyContent="space-between" gap={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onDialogClose}
              disabled={submitting}
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : undefined
              }
              sx={{
                bgcolor: color,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: tone === "danger" ? PURPLE_HOVER : "#154A33" },
                "&.Mui-disabled": {
                  bgcolor: submitting ? color : undefined,
                  color: submitting ? "#fff" : undefined,
                  opacity: submitting ? 0.85 : undefined,
                },
              }}
            >
              {submitBtnLabel}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
